// docs/71: one hero's gear as the inventory reads it — every carried item with its catalogue card
// and rules shape, what is worn by slot, the pack and its capacity, the hero's numbers now (the
// sim's own `derive_stats` over what is worn), and a preview of the numbers after any change.
import { useCallback, useMemo } from 'react';
import { useDyeOf, useHeroCallings, useHeroPacks, useHeroProgress, useItemUpgrades, useMyTrades, useWorldHeroes, useWorldItems } from '../../core/subscriptions/hooks';
import { getBridge } from '../../core/encounter/session';
import { tsToMs } from '../../core/net/connection';
import type { ItemCard } from '../../core/sim/items';
import type { PreviewStats } from '../../core/sim/WasmSimBridge';
import { catalogueGearDef, gearDef, PACK_SIZE, PACKS, rarityOf, upgradeSegs, type Carried, type GearDef, type Rarity } from '../../core/sim/gear';
import { catalogueItem, type CatalogueItem } from '../../core/items/catalogue';
import { useRelicNames } from '../../core/items/live';
import type { SetHolding } from '../../core/items/describe';

const cards = new Map<string, ItemCard | null>();
/** The catalogue card for a def at a tier (cached; undefined until the wasm is up). */
export function cardOf(def: number, tier: number): ItemCard | undefined {
  const key = `${def}:${tier}`;
  if (cards.has(key)) return cards.get(key) ?? undefined;
  const b = getBridge();
  if (!b) return undefined;
  let c: ItemCard | null = null;
  try { c = b.itemCard(def, tier) ?? null; } catch { c = null; }
  cards.set(key, c);
  return c ?? undefined;
}

const NO_DEF: GearDef = { slots: [], twoHanded: false, classMask: 0xff, consumable: false };

/** One carried item with everything the inventory draws. */
export interface GearItem extends Carried {
  defId: number;
  tier: number;
  relic: boolean;
  rarity: Rarity;
  card?: ItemCard;
  /** docs/72: the catalogue entry, for pieces with ids ≥ 1000. */
  cat?: CatalogueItem;
  /** A relic's own name and lore from the storyteller, when the module writes them. */
  relicName?: string;
  relicLore?: string;
  acquiredMs: number;
  /** In one of my open trade offers. */
  offered: boolean;
  /** docs/112: the upgrades laid on this piece, in the order they were applied (a repeat is a rank). */
  upgrades: number[];
  /** Gold spent upgrading it (a quarter comes back when it is sold). */
  upgradeSpent: number;
  /** docs/149: the dye it is in (0: undyed), as it was last worn. */
  dye: number;
}

type HeroRow = ReturnType<typeof useWorldHeroes>[number];

export interface HeroGear {
  hero?: HeroRow;
  items: GearItem[];
  worn: Map<number, GearItem>;
  pack: GearItem[];
  capacity: number;
  /** Index into `PACKS`. */
  grade: number;
  stats?: PreviewStats;
  /** docs/110: the defs this hero owns (worn or packed) and wears, for the set lines. */
  holding: SetHolding;
  /** The numbers wearing exactly these `[slot, def, tier, …]` triples (with their upgrades, `upgradeSegs`). */
  previewWorn(worn: number[], ups?: number[]): PreviewStats | undefined;
}

export function useHeroGear(worldId: bigint | undefined, heroId: bigint | undefined): HeroGear {
  const heroes = useWorldHeroes(worldId);
  const allItems = useWorldItems(worldId);
  const packs = useHeroPacks(worldId);
  const callings = useHeroCallings(worldId);
  const trades = useMyTrades();
  const relics = useRelicNames(worldId);
  const ups = useItemUpgrades(worldId);
  const dyeOf = useDyeOf(worldId);
  const upsBy = useMemo(() => new Map(ups.map((u) => [u.itemId.toString(), { applied: [...u.applied], spent: u.spent }])), [ups]);
  const hero = heroes.find((h) => h.id === heroId);
  const calling = callings.find((c) => c.heroId === heroId);
  // docs/119: what the hero made of their levels counts in every number
  const progress = useHeroProgress(hero?.worldId).find((r) => r.heroId === heroId);
  const offered = useMemo(() => new Set(trades.filter((t) => t.state === 0).flatMap((t) => [...t.giveItems, ...t.askItems].map(String))), [trades]);
  const items = useMemo<GearItem[]>(() => allItems.filter((i) => i.heroId === heroId).map((i) => {
    const card = cardOf(i.def, i.tier);
    const cat = catalogueItem(i.def);
    const def = card ? gearDef(card) : catalogueGearDef(i.def) ?? NO_DEF;
    const slot = i.equippedSlot === undefined || i.equippedSlot === null ? null : i.equippedSlot;
    // docs/72: a relic shows the storyteller's own name and lore once written
    const rw: { name?: string; lore?: string } = (i.relic && relics.get(i.id.toString())) || {};
    return {
      id: i.id, def, slot, name: rw.name || i.name, relic: i.relic, defId: i.def, tier: i.tier,
      rarity: rarityOf(cat ? cat.rarity : i.tier, i.relic || !!cat?.relic), card, cat, ...(rw.name ? { relicName: rw.name } : {}), ...(rw.lore ? { relicLore: rw.lore } : {}),
      acquiredMs: tsToMs(i.acquiredAt) ?? 0, offered: offered.has(i.id.toString()), upgrades: upsBy.get(i.id.toString())?.applied ?? [], upgradeSpent: upsBy.get(i.id.toString())?.spent ?? 0, dye: dyeOf(i.id),
    };
  }).sort((a, b) => (a.id < b.id ? -1 : 1)), [allItems, dyeOf, heroId, offered, relics, upsBy]);
  const worn = useMemo(() => new Map(items.filter((i) => i.slot !== null).map((i) => [i.slot!, i])), [items]);
  const pack = useMemo(() => items.filter((i) => i.slot === null), [items]);
  const p = packs.find((x) => x.heroId === heroId);
  const previewWorn = useCallback((w: number[], u: number[] = []): PreviewStats | undefined => {
    const b = getBridge();
    if (!b || !hero) return undefined;
    try { return b.previewHeroGear(hero.class, hero.species, hero.build, hero.level, calling?.pick ?? 0, calling?.spells ?? [], hero.cards, w, u, progress); } catch { return undefined; }
  }, [hero, calling, progress]);
  const stats = useMemo(() => {
    const worn = items.filter((i) => i.slot !== null);
    return previewWorn(worn.flatMap((i) => [i.slot!, i.defId, i.tier]), upgradeSegs(worn));
  }, [previewWorn, items]);
  const holding = useMemo<SetHolding>(() => ({ owned: new Set(items.map((i) => i.defId)), worn: new Set(items.filter((i) => i.slot !== null).map((i) => i.defId)) }), [items]);
  return { hero, items, worn, pack, capacity: p?.capacity ?? PACK_SIZE, grade: Math.min(PACKS.length - 1, p?.grade ?? 0), stats, holding, previewWorn };
}
